// v3E.3 — Public Emergency ER page (NO AUTH).
// Renders a clean, printable medical summary identified by a signed JWT token in the URL.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Heart, ShieldAlert, AlertOctagon, Printer, Loader2 } from "lucide-react";
import axios from "axios";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectionTitle = ({ children }) => (
    <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.15em] text-rose-700 dark:text-rose-300">{children}</p>
);

const EmergencyPublic = () => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data } = await axios.get(`${PUBLIC_API}/emergency-profile/public/${token}`);
                if (!mounted) return;
                setProfile(data);
            } catch (err) {
                setError(err?.response?.data?.detail || "Could not load");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [token]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 px-4 py-10 dark:from-rose-950 dark:via-zinc-950 dark:to-amber-950" data-testid="emergency-public-page">
            <div className="mx-auto max-w-2xl">
                <header className="mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-rose-600 to-red-600 text-white shadow-lg">
                            <AlertOctagon className="h-6 w-6" />
                        </span>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-rose-700 dark:text-rose-300">PILLS Emergency Profile</p>
                            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">Shared by the patient — verify before treatment</p>
                        </div>
                    </div>
                    <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 dark:border-rose-400/30 dark:bg-transparent dark:text-rose-300 dark:hover:bg-rose-500/10" data-testid="emerg-pub-print">
                        <Printer className="h-3.5 w-3.5" /> Print
                    </button>
                </header>

                {loading ? (
                    <div className="grid h-96 place-items-center">
                        <Loader2 className="h-8 w-8 animate-spin text-rose-500" />
                    </div>
                ) : error ? (
                    <div className="rounded-3xl border border-rose-200 bg-white p-8 text-center text-sm shadow-soft dark:border-rose-400/30 dark:bg-zinc-900" data-testid="emerg-pub-error">
                        <ShieldAlert className="mx-auto h-10 w-10 text-rose-500" />
                        <h2 className="mt-3 text-lg font-semibold text-zinc-900 dark:text-zinc-50">Profile unavailable</h2>
                        <p className="mt-1 text-zinc-600 dark:text-zinc-400">{error === "Invalid or expired token" ? "This link has expired or was revoked by the patient." : "Something went wrong. Please contact the patient directly."}</p>
                    </div>
                ) : (
                    <article className="space-y-5 rounded-3xl border-2 border-rose-200 bg-white p-7 shadow-2xl dark:border-rose-400/30 dark:bg-zinc-900" data-testid="emerg-pub-card">
                        <header className="border-b border-rose-100 pb-4 dark:border-rose-400/20">
                            <SectionTitle>Patient</SectionTitle>
                            <h1 className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50" data-testid="emerg-pub-name">{profile.patient_name}</h1>
                            {profile.pills_customer_id && (
                                <p className="mt-1 font-mono text-[11px] text-zinc-500 dark:text-zinc-400" data-testid="emerg-pub-pillsid">PILLS ID: {profile.pills_customer_id}</p>
                            )}
                        </header>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <SectionTitle>Blood group</SectionTitle>
                                <p className="text-2xl font-bold text-rose-700 dark:text-rose-300" data-testid="emerg-pub-bg">{profile.blood_group || "—"}</p>
                            </div>
                            <div>
                                <SectionTitle>Organ donor</SectionTitle>
                                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{profile.organ_donor ? "Yes" : "No / Unknown"}</p>
                            </div>
                        </div>

                        <div>
                            <SectionTitle>Allergies</SectionTitle>
                            {(profile.allergies || []).length === 0 ? (
                                <p className="text-sm text-zinc-500 dark:text-zinc-400">No known allergies recorded.</p>
                            ) : (
                                <div className="flex flex-wrap gap-2" data-testid="emerg-pub-allergies">
                                    {profile.allergies.map((a) => (
                                        <span key={a} className="rounded-full border border-rose-300 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-800 dark:border-rose-400/30 dark:bg-rose-500/15 dark:text-rose-200">{a}</span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            <SectionTitle>Chronic conditions</SectionTitle>
                            {(profile.chronic_conditions || []).length === 0 ? (
                                <p className="text-sm text-zinc-500 dark:text-zinc-400">None recorded.</p>
                            ) : (
                                <div className="flex flex-wrap gap-2" data-testid="emerg-pub-chronic">
                                    {profile.chronic_conditions.map((c) => (
                                        <span key={c} className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-200">{c}</span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {profile.current_medications_summary && (
                            <div>
                                <SectionTitle>Current medications</SectionTitle>
                                <p className="rounded-xl bg-zinc-50 px-3 py-2 text-sm text-zinc-700 dark:bg-white/[0.04] dark:text-zinc-200" data-testid="emerg-pub-meds">{profile.current_medications_summary}</p>
                            </div>
                        )}

                        {profile.medical_notes && (
                            <div>
                                <SectionTitle>Medical notes</SectionTitle>
                                <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200" data-testid="emerg-pub-notes">{profile.medical_notes}</p>
                            </div>
                        )}

                        <div>
                            <SectionTitle>Emergency contacts</SectionTitle>
                            {(profile.emergency_contacts || []).length === 0 ? (
                                <p className="text-sm text-zinc-500 dark:text-zinc-400">No contacts shared.</p>
                            ) : (
                                <ul className="space-y-2" data-testid="emerg-pub-contacts">
                                    {profile.emergency_contacts.map((c, i) => (
                                        <li key={i} className="flex items-center justify-between rounded-xl border border-zinc-200 bg-zinc-50/60 px-3 py-2 text-sm dark:border-white/10 dark:bg-white/[0.03]">
                                            <div>
                                                <p className="font-semibold text-zinc-900 dark:text-zinc-50">{c.name} {c.is_primary && <span className="ml-1 inline-flex rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold text-rose-700 dark:bg-rose-500/20 dark:text-rose-300">PRIMARY</span>}</p>
                                                <p className="text-[11px] text-zinc-500 dark:text-zinc-400">{c.relation || "—"}</p>
                                            </div>
                                            <p className="font-mono text-sm font-semibold text-zinc-700 dark:text-zinc-200">{c.phone_masked}</p>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <footer className="border-t border-zinc-100 pt-3 text-[10px] text-zinc-400 dark:border-white/10 dark:text-zinc-500">
                            <p>{profile._meta?.disclaimer || "Patient-submitted. Verify before treatment."}</p>
                            <p className="mt-0.5">Last updated: {profile.last_updated ? new Date(profile.last_updated).toLocaleString("en-IN") : "—"}</p>
                        </footer>
                    </article>
                )}

                <p className="mt-6 text-center text-[10px] text-zinc-400 dark:text-zinc-500">
                    Served by <Heart className="-mt-0.5 inline-block h-3 w-3 text-rose-500" /> PILLS Emergency ER Lookup
                </p>
            </div>
        </div>
    );
};

export default EmergencyPublic;
